import React from 'react';
import { connect } from 'react-redux';

import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';

import { saveAdSlot, deleteAdSlot, showAdSlot, hideAdSlot,
		loadSecondaryAds, setGPTCorrelator, trackGAEvent
} from 'Actions/adsActions';

import adSelector from 'Selectors/adSelector';

import AFSAdComponent from 'Components/ads/afsAdComponent';
import InContentAdComponent from 'Components/ads/inContentAdComponent';
import SecondaryAdComponent from 'Components/ads/secondaryAdComponent';

import * as Swipe from 'Utils/swipe';

const adRefreshTime = 45 * 1000; // 45 seconds
let prebid_slot_used = {};

/**
 * Component to render Ads
 * We can pass the following props:
 *  - adType int *[mandatory] indicates what ad we want to render. see bellow for types
 *  - adName string *[mandatory] is the name of the ad and the id of the container div
 *  - search object *[mandatory for AFS] for AFS we need to pass some info about the ad (is top, query, page)
 *  - fallbackAdType array [optional] in case the adType is not defined we'll check for any of these adTypes
 *  - className string [optional] className by type are automatically added but if we need to include any extra we can use this prop
 *  - tag string [optional] if we want the ad to wrapped on a tag
 *  - isFetching bool [optional] if refresh depends upon we are not fetching data at the moment, like Bottom_Content_Rectangle
 *
 * Ads active for a site are defined in: topifyPresets.forumSite.dfpTags
 * adTypes will match those on topifyPresets but adType=0 which is AFS and doesn't need Ad Unit
 *
 * Ad Types in app can have the following types:
 * - 0 - 'AFS': AdSense For Search
 * - 1 - 'InContentLeaderboard': usually 320x50 ad inserted every # threads or posts
 * - 2 - 'InContentBottomRectangle': Big 300x250 at the bottom of some pages
 * - 3 - 'FixedBottomLeaderboard': 320x50 ad fixed at the bottom of the viewport and always visible
 * - 4 - 'Insterstitial': Interstitial triggered once after 2 pageviews
 * - 5 - 'InContentTopicAd': ad inserted in index page between categories and at the top of threads (usually fallbackAdType = [1])
 * - 6 - 'beaconLastPost': beacon triggered on some sites
 * - 50 - 'PortalInsurance1': Insurance ad on portal page
 * - 51 - 'PortalInsurance2': Insurance ad on portal page
 * - 52 - 'PortalInsurance3': Insurance ad on portal page
 * - 53 - 'PortalInsurance4': Insurance ad on portal page
 * - 54 - 'PortalInsurance5': Insurance ad on portal page
 * - 101 - 'InContentHomePage': Insurance fake forum in index page
 * - 105 - 'InContentTopicAd_1': ShareThrough ad every # posts (usually fallbackAdType = [1])
 * - 205 - 'InContentTopicAd_2': Yieldmo Hyperscroll/Outstream ad every # posts (usually fallbackAdType = [1])
 * - 305 - 'InContentTopicAd_3': NOT USED
 * - 505 - 'InContentTopicCampaign': Sharethrough Video Campaigns. Top of threads (bellow adType=5) and posts
 *
 */
class AdComponent extends React.Component {
	constructor( props ) {
		super( props );

		this.visibilityObserver = null;
		this.theAdElement = null;
		this.refreshTimer = null;
		this.adUnit = null;
		this.ad_slot = {};
		this.propsChanged = false;
		this.lastRefreshTime = 0;
		this.adType = null;
		this.state = {
			adHidden: false
		};
	}

	componentWillMount() {
		this.adType = parseInt( this.props.adType );

		if( this.adType === 0 ) { // search AFS
			this.adUnit = this._getAFSAdUnit();
			this.props.dispatch( saveAdSlot( this.props.adName ) );

		} else {
			// gets potential adTypes passed
			this.adTypes = [ this.props.adType, ...( this.props.fallbackAdType ? this.props.fallbackAdType : [] ) ];
			// ensure everything is int
			this.adTypes.forEach( ( adType, i ) => this.adTypes[ i ] = parseInt( adType ) );

			let units = this._get_AdUnits(),
				ad_unit = this._find_adType( units, 0 );

			if( ad_unit ) {
				this.adUnit = this._parseAd( ad_unit );
				this.props.dispatch( saveAdSlot( this.props.adName ) );
			}
		}

		this._checkPerms();
	}

	componentDidMount() {
		topifyConsole.log( 'Mounted ad:', this.props.adName );
		if( this._isAlwaysShown() ) {
			if( this.adUnit ) {
				// Some ads are always visible
				this.props.dispatch( showAdSlot( this.props.adName ) );
				this.displayAdUnit();
			}

		} else {
			// Use IntersectionObserver if is available
			if( window.IntersectionObserver ) {
				const options = {
					rootMargin: '30% 0%', // 30% of the viewport above and below
					threshold: 0 // just when it shows up
				};

				this.visibilityObserver = new IntersectionObserver( this._checkAdVisibility, options );

				if( this.visibilityObserver && this.theAdElement ) {
					this.visibilityObserver.observe( this.theAdElement );
				}

			} else {
				// others just show or hide per scroll position
				window.addEventListener( "scroll", this.handleScroll, { passive: true } );
				this._checkAdVisibility();
			}
		}
	}

	componentWillReceiveProps( nextProps ) {
		if( nextProps.ads.resetHiddenAds !== this.props.ads.resetHiddenAds ) { // show those ads hidden
			this._resetAdHidden();
		}
		if(
			nextProps.ads.resetHiddenAds !== this.props.ads.resetHiddenAds ||
			nextProps.ads.noAdsPermissions !== this.props.ads.noAdsPermissions ||
			nextProps.ads.secondaryAds !== this.props.ads.secondaryAds
		) {
			this._checkPerms( nextProps );
		}

		// If we have closed the overlays open or the document is on focus again
		// if the ad_slot exists and it's a refreshable ad
		// then re-start refresh
		if(
			( this.ad_slot.getSlotElementId || nextProps.ads.secondaryAds ) &&
			this._noRepetitiveRefreshAd() === false &&
			( this.overlayElementsChanged( nextProps ) || nextProps.ads.documentVisible !== this.props.ads.documentVisible ) &&
			this.overlayElementsOpen( nextProps ) === false &&
			nextProps.ads.documentVisible
		) {
			this.lastRefreshTime = 0;
			this.scheduleRefresh( 500 );
		}
	}

	shouldComponentUpdate( nextProps, nextState ) {
		this.propsChanged = (
			nextState.adHidden !== this.state.adHidden ||
			nextProps.ads.noAdsPermissions !== this.props.ads.noAdsPermissions ||
			nextProps.ads.secondaryAds !== this.props.ads.secondaryAds ||
			(
				typeof nextProps.ads.adSlots[ nextProps.adName ] !== 'undefined' &&
				nextProps.ads.adSlots[ nextProps.adName ] !== this.props.ads.adSlots[ nextProps.adName ]
			)
		);

		if( this.propsChanged ) {
			this.lastRefreshTime = 0;
		}

		return this.propsChanged;
	}

	overlayElementsChanged = ( nextProps ) => {
		return !isEqual( nextProps.overlayElements, this.props.overlayElements );
	};

	overlayElementsOpen = ( props = this.props ) => {
		// do we have anything open?
		return Object.values( props.overlayElements ).some( v => v );
	};

	componentWillUpdate() {
		// if we allowed to swipe to hide, remove events
		if( this._swipeToHide() && this.theAdElement ) {
			Swipe.kill( this.theAdElement );
	   }
	}

	componentDidUpdate() {
		if(
			this.adUnit &&
			this.state.adHidden === false &&
			this._noAdsPermissions() === false &&
			this.props.ads.adSlots[ this.props.adName ] === true
		) {
			this._addPrebidAdUnit( this.adUnit );
			topifyConsole.log( 'pbjs.adUnits', ( this._isPrebid() ? pbjs.adUnits : 'no prebid' ) );
			this.displayAdUnit();
		} else {
			if( this.props.ads.adSlots[ this.props.adName ] === true ) {
				this.props.dispatch( hideAdSlot( this.props.adName ) );
			}
			this.removeAdUnit();
		}
		// do we allow to swipe to hide?
		if( this._swipeToHide() && this.theAdElement ) {
			Swipe.init( this.theAdElement, ( code ) => { this._swipedThenHide( code ) } );
		}
	}

	componentWillUnmount() {
		// disconnecting visibilityObserver
		if( this.visibilityObserver ) {
			this.visibilityObserver.disconnect();

		} else {
			this.handleScroll.cancel();
			// removing scroll event
			window.removeEventListener( "scroll", this.handleScroll, { passive: true } );
		}

		// if we allowed to swipe to hide, remove events
		if( this._swipeToHide() && this.theAdElement ) {
			Swipe.kill( this.theAdElement );
		}

		// Remove the ad unit
		this.removeAdUnit();
		this.props.dispatch( deleteAdSlot( this.props.adName ) );
	}

	displayAdUnit() {
		if( this.adType === 0 ) { // Search AFS
			if( this._noAdsPermissions() === false ) {
				this.displayAFS();
			}
			return;
		}
		// We already have the ad and nothing have changed
		if( this.ad_slot.getSlotElementId && this.propsChanged === false ) {
			return;
		}
		// if we are temporary not showing ads, keep on it expecting a change
		if( this._noAdsPermissions() || this.props.ads.secondaryAds || this.state.adHidden ) {
			this.scheduleRefresh();
			return;
		}

		let adName = this.props.adName,
			adType = this.adType,
			ad = this.adUnit;

		googletag.cmd.push( () => {
			if( googletag.defineSlot && googletag.display ) {
				if( !this.ad_slot || Object.keys( this.ad_slot ).length <= 0 ) {
					if( adType === 4 ) {
						this.ad_slot = googletag.defineSlot( ad.path, ad.size, adName ).setCollapseEmptyDiv( true, true ).addService( googletag.pubads() );
					} else {
						let sizeAd = this._getAdSizes();
						this.ad_slot = googletag.defineSlot( ad.path, sizeAd, adName ).setCollapseEmptyDiv( !this._isFluid() ).addService( googletag.pubads() );
					}
					googletag.display( adName );
				}
				this.scheduleRefresh();
			}
		} );
	}

	refreshAdUnit() {
		let adName = this.props.adName,
			ad_slot = this.ad_slot,
			timestamp = new Date().getTime();

		// The DOM is not there
		if( this.theAdElement === null ) {
			return;
		}

		// Do we skip refresh...?
		if( this.props.ads.documentVisible === false || this.overlayElementsOpen() ) {
			topifyConsole.log( 'Ad not visible, skip refresh:', this.props.adName );
			return;
		}
		// If the adSlot wasn't generated when we need it
		if( !ad_slot.getSlotElementId && !this.props.ads.secondaryAds ) {
			this.displayAdUnit();
			return;
		}
		// only refresh at most every adRefreshTime
		if( ( timestamp - adRefreshTime ) < this.lastRefreshTime ) {
			this.scheduleRefresh();
			return;
		}

		// in some cases we don't want to reload ads
		if( this._avoidReloadAdUnit() ) {
			return;
		}
		// some ads don't need to be refreshed
		if( this._noRepetitiveRefreshAd() && this.ad_slot.getSlotElementId() ) {
			googletag.cmd.push( () => {
				googletag.display( ad_slot.getSlotElementId() );
				googletag.pubads().refresh( [ ad_slot ], { changeCorrelator: this.props.ads.changeGPTCorrelator } );
				if( this.props.ads.changeGPTCorrelator ) {
					this.props.dispatch( setGPTCorrelator( false ) );
				}
			} );
			return;
		}

		this.lastRefreshTime = 0;

		// Refresh
		if( this.props.ads.secondaryAds ) { // If we use secondary ads... Rubicon standalone
			let type = 'rubicon',
				secAdsProps = {
					size: this.adUnit.size,
					container: `SecondaryAd_${ this.props.adName }`
				};
			topifyConsole.info( "Secondary Ads", type, secAdsProps );
			let callBack = () => {
					topifyConsole.log( 'Secondary Ad loaded!! (Rubicon)', this.props.adName );
					this.theAdElement.classList.remove( 'hidden' );
					this.lastRefreshTime = timestamp - 1000
				},
				onError = () => {
					topifyConsole.warn( 'Secondary Ad failed!! (Rubicon)', this.props.adName );
					this.theAdElement.classList.add( 'hidden' );
					this.removeAdUnit();
				};

			this.props.dispatch( loadSecondaryAds( type, secAdsProps, callBack, onError ) );
			// leave 2 sec before regular refresh
			this.scheduleRefresh( adRefreshTime - 2000 );

		} else if( this._isPrebid() ) { // If it's prebid
			this.theAdElement.classList.remove( 'hidden' );
			// Refresh individual ad
			pbjs.que.push( () => {
				topifyConsole.info( 'using pbjs', ad_slot.getSlotElementId() );

				// if we have come back form no ads space, we can have an ad defined, display it
				if( !document.getElementById( ad_slot.getSlotElementId() ).getAttribute( 'data-google-query-id' ) ) {
					googletag.display( ad_slot.getSlotElementId() );
				}

				// just in case we'll refresh after the refreshtime
				this.scheduleRefresh( adRefreshTime );

				// requestBids
				pbjs.requestBids( {
					timeout: window.PREBID_TIMEOUT,
					adUnitCodes: [ ad_slot.getSlotElementId() ],
					bidsBackHandler: () => {
						topifyConsole.info( 'getBidResponsesForAdUnitCode for:', ad_slot.getSlotElementId(), pbjs.getBidResponsesForAdUnitCode( ad_slot.getSlotElementId() ) );

						this.lastRefreshTime = pbjs.getBidResponsesForAdUnitCode( ad_slot.getSlotElementId() ).bids.length > 0 ? timestamp - 1000 : timestamp - 10000;
						topifyConsole.log( 'REFRESH:', this.lastRefreshTime, ad_slot.getSlotElementId() );

						googletag.cmd.push( () => {
							if( googletag.pubads().clear ) {
								googletag.pubads().clear( [ ad_slot ] );
							}
						} );

						// Let's trigger the refresh
						this.scheduleRefresh( adRefreshTime );

						try {
							pbjs.setTargetingForGPTAsync( [ ad_slot.getSlotElementId() ] );
						} catch( e ) {
							topifyConsole.error( 'pbjs.setTargetingForGPTAsync', adName, ad_slot.getSlotElementId() );
						}

						googletag.cmd.push( () => {
							googletag.pubads().refresh( [ ad_slot ], { changeCorrelator: this.props.ads.changeGPTCorrelator } );
							if( this.props.ads.changeGPTCorrelator ) {
								this.props.dispatch( setGPTCorrelator( false ) );
							}
						} );
					}
				} );
			} );

		} else { // If prebid is not ready yet or it's not present we load ads without it
			topifyConsole.info( 'using googletag', ad_slot.getSlotElementId() );
			this.theAdElement.classList.remove( 'hidden' );
			googletag.cmd.push( () => {
				if( googletag.pubads().clear ) {
					googletag.pubads().clear( [ ad_slot ] );
				}
			} );

			if( !document.getElementById( ad_slot.getSlotElementId() ).getAttribute( 'data-google-query-id' ) ) {
				googletag.cmd.push( () => {
					googletag.display( ad_slot.getSlotElementId() );
				});
			}
			googletag.cmd.push( () => {
				this.lastRefreshTime = timestamp - 1000;
				this.scheduleRefresh( adRefreshTime );

				googletag.pubads().refresh( [ ad_slot ], { changeCorrelator: this.props.ads.changeGPTCorrelator } );
				if( this.props.ads.changeGPTCorrelator ) {
					this.props.dispatch( setGPTCorrelator( false ) );
				}
			} );
		}
	}

	/**
	 * Schedules an ad refresh
	 *
	 * @param extraTime if we want to add an extra time
	 */
	scheduleRefresh( extraTime = 0 ) {
		if( this.refreshTimer ) {
			clearTimeout( this.refreshTimer );
			this.refreshTimer = undefined;
		}
		this.refreshTimer = setTimeout( () => {
			this.refreshAdUnit();
		}, ( 1000 + extraTime ) );
	}

	removeAdUnit() {
		clearTimeout( this.refreshTimer );
		this.refreshTimer = undefined;

		// remove ad slots
		let adName = this.props.adName;
		topifyConsole.log( 'removeAdUnit', this.props.adName );
		if( this.ad_slot && Object.keys( this.ad_slot ).length !== 0 ) {
			googletag.cmd.push( () => {
				if( googletag.destroySlots ) {
					googletag.destroySlots( [ this.ad_slot ] );
				}
			} );
			this.ad_slot = {};
		}

		if( this._isPrebid() ) {
			// Delete from prebis adUnits
			let adIndex = prebid_adUnits.findIndex( function( ad ) {
				return ad.code === adName
			} );

			if( adIndex >= 0 ) {
				// Remove adUnits from tracking
				if( prebid_adUnits[ adIndex ] && prebid_adUnits[ adIndex ].Topifytype ) {
					delete prebid_slot_used[ prebid_adUnits[ adIndex ].Topifytype ];
				}

				// Remove adUnits from prebid adUnits
				if( pbjs.removeAdUnit ) {
					pbjs.removeAdUnit( adName );
				}
				prebid_adUnits.splice( adIndex, 1 );
			}
		}
	}

	displayAFS() {
		if( this.props.ads.adSlots[ this.props.adName ] ) {
			this.adUnit.pageOptions.adPage = this.props.search.page; // page
			this.adUnit.pageOptions.query = this.props.search.searchQuery; // search query

			// if we have all the values we need trigger the ads
			if(
				typeof _googCsa === 'function' &&
				this.adUnit.pageOptions.pubId &&
				this.adUnit.pageOptions.channel &&
				this.adUnit.pageOptions.query
			) {
				_googCsa( 'ads', this.adUnit.pageOptions, this.adUnit.adblock );
			}
		}
	}

	handleScroll = throttle( () => this._checkAdVisibility(), 200 );

	_swipedThenHide( code ) {
		this.props.dispatch( trackGAEvent( false, 'swipeHide', 'userAction', 'ads' ) );
		this.setState( { adHidden: true } );
	}

	_resetAdHidden() {
		if( this.state.adHidden === true && this.props.ads.adSlots[ this.props.adName ] === false ) {
			this.props.dispatch( showAdSlot( this.props.adName ) );
		}
		this.setState( { adHidden: false } );
	}

	_checkAdVisibility = ( entries ) => {
		// We are in a noAds subforum
		if( this._noAdsPermissions() || this.state.adHidden ) {
			return;
		}

		let entry = this.visibilityObserver && entries.length ? entries[ 0 ] : null;

		if( this._isAlwaysShown() || this._isPositionOk( entry ) ) {
			if( this.props.ads.adSlots[ this.props.adName ] !== true ) {
				this.props.dispatch( showAdSlot( this.props.adName ) );
			}

		} else {
			if( this.props.ads.adSlots[ this.props.adName ] === true ) {
				this.props.dispatch( hideAdSlot( this.props.adName ) );
				this.lastRefreshTime = null;
			}
		}
	};

	_isPositionOk( ad = null ) {
		let positionOk = false;

		if( ad ) { // Using IntersectionObserver
			positionOk = ( ad.isIntersecting && ad.intersectionRatio > 0 );

		} else { // old method
			let rect = this.theAdElement ? this.theAdElement.getBoundingClientRect() : null,
				viewport = (window.innerHeight || document.documentElement.clientHeight);
			positionOk = rect && (
				rect.top >= 0 && ( rect.top - ( viewport / 3 ) ) <= viewport ||
				rect.bottom <= 0 && ( rect.bottom + ( viewport / 3 ) ) >= 0
			);
		}

		// Bottom endContentRectangle will only load when the page is fully loaded
		if( parseInt( this.adType ) === 2 && this.props.isFetching === true ) {
			return false;
		}

		return positionOk;
	}

	_get_AdUnits() {
		const forumSite = this.props.topifyPresets.forumSite;

		if( forumSite.dfpTags ) {
			return JSON.parse( forumSite.dfpTags );
		}
	}

	_find_adType( units, i ) {
		let ad_unit,
			adType = parseInt( this.adTypes[ i ] );

		if( units.length > 0 ) {
			ad_unit = units.find( function( ad ) {
				return ( parseInt( ad.at_id ) === adType );
			} );
		}

		if( ad_unit ) {
			this.adType = parseInt( adType );
			ad_unit.fda_path = ad_unit.fda_path.trim();
		} else if( i < this.adTypes.length - 1 ) {
			return this._find_adType( units, ( i + 1 ) );
		}

		return ad_unit;
	}

	_avoidReloadAdUnit() {
		return (
			this.props.ads.adSlots[ this.props.adName ] === false ||
			this.adType === 0 || // AFS
			this._noAdsPermissions() ||
			this.state.adHidden
		);
	}

	_noRepetitiveRefreshAd() {
		return (
			( !this.props.ads.secondaryAds && this._isYieldmo() ) ||  // inCcontentTopicAd && Yieldmo
			this.adType === 4 || // Interstitial
			this.adType === 101 || // InContentHomePage
			( this.adType === 105 && this.props.topifyPresets.forumSite.f_sharethrough ) || // ShareThrough
			( this.adType === 505 && this.props.topifyPresets.forumSite.f_sharethrough ) || // 505: 'InContentTopicCampaign' - ShareThrough
			( this.adType >= 50 && this.adType <= 59 ) // Insurance ads for portal page
		);
	}

	_addPrebidAdUnit( ad ) {
		// Skip if we are in search or it's don't need prebid
		if(
			ad === null
			|| !ad.size
			|| this._isPrebid() === false
			|| this.adType === 0
			|| this._noAdsPermissions()
			|| this.props.ads.secondaryAds
			|| this.state.adHidden
		) {
			return;
		}

		let adName = this.props.adName,
			prebid_slot_used_already = false,
			unit = '',
			size = new RegExp( ad.size[ 0 ] + 'x' + ad.size[ 1 ] + '_', 'ig' );

		// already used
		for( let u_size in prebid_slot_used ) {
			if( prebid_slot_used[ u_size ] === adName ) {
				prebid_slot_used_already = true;
			}
		}

		if( prebid_slot_used_already ) {
			return;
		}

		switch( this.adType ) {
			// 1 'inContentLeaderboard'
			// 2 'endContentRectangle'
			// 3 'bottomFixedLeaderboard'
			// 5 'inContentTopicAd'
			// 101 'InContentHomePage'
			// 105 'inContentTopicAd_1'
			// 205: 'inContentTopicAd_2'
			// 305: 'inContentTopicAd_3'
			// 505: 'InContentTopicCampaign' - ShareThrough
			default:
				let u = prebid_adUnits_def.find(
					function( the_ad ) {
						let unit = the_ad.code.match( size );
						return ( unit && typeof prebid_slot_used[ the_ad.code ] === 'undefined' );
					}
				);

				unit = u && u.code ? JSON.parse( JSON.stringify( u ) ) : {};
				break;
		}

		if( unit.code ) {
			unit.Topifytype = unit.code + '';
			unit.code = adName;

			let currentAdUnits = pbjs.adUnits || [];

			if( currentAdUnits.findIndex( function( ad ) { return ad.code === adName } ) < 0 ) {
				pbjs.que.push( () => {
					if( pbjs.addAdUnits ) {
						pbjs.addAdUnits( unit );
					}
				} );
				if( prebid_adUnits.findIndex( function( ad ) { return ad.code === adName } ) < 0 ) {
					prebid_adUnits.push( unit );

					// Tracking which adUnits is being used
					prebid_slot_used[ unit.Topifytype ] = adName;
					topifyConsole.log( 'prebid_slot_used: ', prebid_slot_used );
				}
			}
		}
	}

	_parseAd = ( ad_unit ) => {
		let re = /(?:\/(\d+)\/)?(?:([_a-zA-Z0-9]+)_)?((?:\d+x\d+)|(?:fluid))(?:_([_a-zA-Z0-9]+))?/ig;

		let _adPath = re.exec( ad_unit.fda_path ) || [];
		_adPath[ 'path' ] = ad_unit.fda_path;

		// Formatting sizes
		if( _adPath[ 3 ] ) {
			let size = _adPath[ 3 ].split( 'x' );
			if( ( parseInt( size[ 0 ] ) && parseInt( size[ 1 ] ) ) || size[ 0 ] === 'fluid' ) {
				_adPath[ 'size' ] = size[ 0 ] === 'fluid' ? [ size[ 0 ] ] : [ parseInt( size[ 0 ] ), parseInt( size[ 1 ] ) ];
			}
		} else {
			// Default sizes
			switch( parseInt( ad_unit.at_id ) ) {
				case 1: // 'inContentLeaderboard'
					_adPath[ 'size' ] = [ 320, 50 ];
					break;
				case 2: //'endContentRectangle'
					_adPath[ 'size' ] = [ 300, 250 ];
					break;
				case 3: // 'bottomFixedLeaderboard'
					_adPath[ 'size' ] = [ 320, 50 ];
					break;
				case 4: // 'interstitial'
				case 101: // 'InContentHomePage'
				case 505: // 'InContentTopicCampaign' - ShareThrough
					_adPath[ 'size' ] = [ 1, 1 ];
					break;
				case 5: // 'inContentTopicAd'
				case 105: // 'inContentTopicAd_1'
				case 205: // 'inContentTopicAd_2' - yieldmo Hyperscroll/Outstream
				case 305: // 'inContentTopicAd_3'
					_adPath[ 'size' ] = [ 320, 150 ];
					break;
			}
		}

		return _adPath;
	};

	_getAFSAdUnit() {
		const forumSite = this.props.topifyPresets.forumSite;

		// AFS - page options base values
		let adUnit = {
			pageOptions: {
				pubId: forumSite.f_afs_publisherid,
				channel: forumSite.f_afs_channelid,
				query: '',
				hl: 'en',
				adtest: ( process.env.NODE_ENV === 'production' ? 'off' : 'on' ),
				adPage: 1,
				location: false,
				siteLinks: false
			},
			// AFS - adblock options base values
			adblock: {
				container: this.props.adName,
				detailedAttribution: false,
				// style
				width: ( document.getElementById( 'header' ).getBoundingClientRect().width - 20 ) + 'px',
				fontSizeAttribution: 8,
				fontSizeAnnotation: 8,
				fontSizeTitle: 10,
				fontSizeDescription: 9,
				fontSizeDomainLink: 11,
				colorBackground: '#CCCCCC',
				colorBorder: '#636363',
				colorAdSeparator: '#636363'
			}
		};

		// Other settings
		if( this.props.search.isTop === true ) {
			delete adUnit.adblock.number;
			adUnit.adblock.maxTop = 1;
		} else {
			delete adUnit.adblock.maxTop;
			adUnit.adblock.number = 1;
		}

		return adUnit;
	}

	_getAdUnit() {
		if( this.props.ads.secondaryAds ) {
			switch( this.adType ) {
				case 1: // 'inContentLeaderboard'
				case 2: // 'endContentRectangle'
				case 3: // 'bottomFixedLeaderboard'
					return <SecondaryAdComponent key={ this.props.adName } adName={ this.props.adName } type="rubicon" />;

				default:
					return null
			}
		} else {
			switch( this.adType ) {
				case 0: // 'AFS adsense for search'
					return <AFSAdComponent key={ this.props.adName } adName={ this.props.adName } />;

				case 1: // 'inContentLeaderboard'
				case 2: // 'endContentRectangle'
				case 3: // 'bottomFixedLeaderboard'
				case 5: // 'inContentTopicAd'
				case 4: // 'interstitialAd'
				case 101: // 'InContentHomePage'
				case 105: // 'inContentTopicAd_1' - ShareThrough
				case 205: // 'inContentTopicAd_2' - yieldmo Hyperscroll/Outstream
				case 305: // 'inContentTopicAd_3'
				case 505: // 'InContentTopicCampaign' - ShareThrough
					return <InContentAdComponent key={ this.props.adName } adName={ this.props.adName } />;

				default:
					return null
			}
		}
	}

	_getAdUnitClasses() {
		let className = 'ad-card ';

		className += this.props.ads.secondaryAds ? 'secondary-ad-container ' : '';

		switch( this.adType ) {
			case 0: // AFS
				className += ( this.props.search.isTop === true ? 'search-top-ad ' : 'search-ad ' );
				break;

			case 1: // 'inContentLeaderboard'
				className += 'in-content-leaderboard-ad ';
				break;

			case 2: //'endContentRectangle'
				className += 'bottom-ad ';
				break;

			case 3: //'bottomFixedLeaderboard'
				className += '';
				break;

			case 4: // 'interstitialAd'
				className += 'interstitial-ad ';
				break;

			case 5: // 'inContentTopicAd'
			case 105: // 'inContentTopicAd_1' - ShareThrough
			case 205: // 'inContentTopicAd_2' - yieldmo Hyperscroll/Outstream
			case 305: // 'inContentTopicAd_3'
			case 505: //'InContentTopicCampaign' - ShareThrough
				className += 'in-content-ad ';
				break;

			case 101: // 'InContentHomePage'
				className += 'homepage-in-content-ad ';
				break;

			default:
				className += '';
				break;
		}

		return className + ( this.props.className ? this.props.className + ' ' : '' ) + ( this._noAdsPermissions() === true || this.state.adHidden === true ? 'hidden ' : '');
	}

	_getAdSizes() {
		switch( true ) {
			case ( this._isYieldmo() && this.adType === 205 ):
				return [ [ 320, 50 ], [ 320, 186 ] ];

			case this._isYieldmo():
				return [ [ 320, 186 ], [ 320, 50 ], [ 320, 150 ], 'fluid' ];

			case this._isFluid():
				return [ this.adUnit.size, 'fluid' ];

				default:
					return this.adUnit.size;
		}
	}

	_isFluid() {
		return (
			this.adType === 2 || //'endContentRectangle'
			this.adType === 5 || // 'inContentTopicAd' - yieldmo
			this.adType === 101 || // 'InContentHomePage'
			this.adType === 105 || // 'inContentTopicAd_1' - ShareThrough
			this.adType === 505 || // 'InContentTopicCampaign' - ShareThrough
			( this.adType >= 50 && this.adType <= 59 ) // Insurance ads for portal page
		);
	}

	_isYieldmo() {
		return (
			parseInt( this.props.topifyPresets.forumSite.f_yieldmo ) === 1 &&
			(
				this.adType === 5 || // 'inContentTopicAd' - yieldmo
				this.adType === 205 // 'inContentTopicAd2' - yieldmo Hyperscroll/Outstream
			)
		);
	}

	_isPrebid() {
		return (
			!!( typeof pbjs !== 'undefined' && pbjs )
			&& !this._isYieldmo()
			&& !( this.adType === 105 && this.props.topifyPresets.forumSite.f_sharethrough )// ShareThrough
			&& !( this.adType === 505 && this.props.topifyPresets.forumSite.f_sharethrough )// 'InContentTopicCampaign' - ShareThrough
			&& !( this.adType === 4 && this.props.ads.showInterstitial ) // interstitial
			&& !( this.adType >= 50 && this.adType <= 59 ) // Insurance ads for portal page
		);
	}

	_isAlwaysShown() {
		// We want some ads to be shown always and skip the in-viewport check
		return (
			this.adType === 0 // AFS
			|| this.adType === 3 // bottomFixedLeaderboard
			|| ( this.adType === 4 && this.props.ads.showInterstitial ) // interstitial
			|| ( !this.props.ads.secondaryAds && this._isYieldmo() ) // inCcontentTopicAd && Yieldmo
			|| this.adType === 101 // InContentHomePage
			|| ( this.adType === 105 && this.props.topifyPresets.forumSite.f_sharethrough )// ShareThrough
			|| ( this.adType === 505 && this.props.topifyPresets.forumSite.f_sharethrough )// 'InContentTopicCampaign' - ShareThrough
			|| ( this.adType >= 50 && this.adType <= 59 ) // Insurance ads for portal page
		);
	}

	_doesItNeedDOM() {
		return (
			!( this.adType >= 50 && this.adType <= 59 ) // portal ads
		);
	}

	_noAdsPermissions( props = this.props ) {
		return (
			props.ads.noAdsPermissions &&
			!( this.adType >= 50 && this.adType <= 59 ) && // insurance portal ads
			this.adType !== 101 // InContentHomePage
		);
	}

	_swipeToHide() {
		return this.adType === 3; // Fixed_Bottom_Leaderboard
	}

	_checkPerms( props = this.props ) {
		// Remove the adUnit
		if( props.ads.secondaryAds || this._noAdsPermissions( props ) || this.state.adHidden ) {
			this.removeAdUnit();
		}

		// Hide or Show ads depending on permissions
		if( this._noAdsPermissions( props ) || this.state.adHidden ) {
			if( props.ads.adSlots[ props.adName ] === true ) {
				props.dispatch( hideAdSlot( props.adName ) );
			}
		} else if( props.ads.adSlots[ props.adName ] === false && this._isAlwaysShown() ) {
			props.dispatch( showAdSlot( props.adName ) );
		}
	}

	render() {
		if(
			this._doesItNeedDOM() === false ||
			this.adUnit === null ||
			(
				typeof this.adUnit.index === 'undefined' &&
				typeof this.adUnit.adblock === 'undefined'
			)
		) {
			return null;
		}

		const adUnit = this._getAdUnit(),
			className = this._getAdUnitClasses();

		if( adUnit === null ) {
			return null;
		}

		let content = null,
			styles = {};

		if(
			typeof this.props.ads.adSlots[ this.props.adName ] === 'undefined' ||
			this.props.ads.adSlots[ this.props.adName ] !== true
		) {
			content = (<div id={ this.props.adName + '_container' } className={ className } ref={ ( ad ) => this.theAdElement = ad } style={ styles }/> );
		} else {
			content = (
				<div id={ this.props.adName + '_container' } className={ className } ref={ ( ad ) => this.theAdElement = ad } style={ styles }>
					{ adUnit }
				</div>
			);
		}

		// If we have a wrapper tag
		if( this.props.tag ) {
			const CustomTag = `${this.props.tag}`;
			return ( <CustomTag>{ content }</CustomTag> );

		} else {
			return content;
		}
	}
}

export default connect( adSelector )( AdComponent );



// WEBPACK FOOTER //
// ./src/components/ads/AdComponent.js